/**
 * What every git network command needs from a repository's link (`git`):
 * the credential, the HTTPS user, and the commit author. Shared by workspace
 * repositories (workspaceGitSync.js) and scope repositories (scopeGitSync.js,
 * git sync Stage 2) — moved here from workspaceGitSync.js on 03.10.2026,
 * unchanged.
 */
import { isVaultLockedError } from '../vaultGate';

export const DEFAULT_BRANCH = 'main';

export function branchOfLink(git) {
  return git?.branch || DEFAULT_BRANCH;
}

/** Every provider call takes the link (`git`): it names the account (card 6). */
export async function requireToken(provider, git) {
  // "Other git server": the credential is a Vault item Rust reads itself.
  if (provider.usesVault) {
    if (git?.vaultItemId) return '';
    const err = new Error(provider.notLinkedCode);
    err.code = provider.notLinkedCode;
    throw err;
  }
  const token = await provider.getToken(git);
  if (!token) {
    const err = new Error(provider.notLinkedCode);
    err.code = provider.notLinkedCode;
    throw err;
  }
  return token;
}

/**
 * The token for a background status check, which must never open a dialog:
 * a token behind a locked Vault answers `{ token: '', vaultLocked: true }`
 * (the check then skips the fetch and the status says "Unlock the Vault to
 * sync"). Any other failure propagates as before.
 */
export async function statusToken(provider, git) {
  try {
    return { token: await provider.getToken(git), vaultLocked: false };
  } catch (e) {
    if (isVaultLockedError(e)) return { token: '', vaultLocked: true };
    throw e;
  }
}

/** What the git network commands get to authenticate: a token, or a Vault item id. */
export function credArgs(provider, token, git) {
  return { token: token || null, username: provider.httpsUsername ?? null, vaultItemId: git?.vaultItemId || null };
}

/** Author fields for git_commit_all / git_merge_remote / git_resolve. */
export async function authorFor(provider, git) {
  const account = await provider.getAccount(git);
  return { authorName: account.login, authorEmail: provider.authorEmail(account.login, account) };
}
